import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';

export default function AdminLoginPage() {
  const { login, isAuthenticated, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('请输入邮箱和密码');
      return;
    }
    setSubmitting(true);
    setError('');
    const ok = await login(email.trim(), password);
    if (ok) {
      navigate('/admin', { replace: true });
    } else {
      setError('邮箱或密码错误，请检查后重试');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#18181B] mb-4">
            <span className="text-[#F59E0B] font-black text-2xl">G</span>
          </div>
          <h1 className="text-xl font-bold text-[#18181B]">GTRA 管理后台</h1>
          <p className="text-sm text-[#6B7280] mt-1">请使用管理员账号登录</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1.5">邮箱</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
                  <i className="ri-mail-line text-[#9CA3AF] text-sm" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@gtraweb3.com"
                  className="w-full pl-9 pr-4 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/30 focus:border-[#F59E0B] bg-white text-[#18181B] placeholder-[#D1D5DB] transition-all"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1.5">密码</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none w-4 h-full">
                  <i className="ri-lock-line text-[#9CA3AF] text-sm" />
                </div>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-9 pr-10 py-2.5 text-sm border border-[#E5E7EB] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F59E0B]/30 focus:border-[#F59E0B] bg-white text-[#18181B] placeholder-[#D1D5DB] transition-all"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(!showPass)}
                  className="absolute inset-y-0 right-3 flex items-center cursor-pointer text-[#9CA3AF] hover:text-[#6B7280]"
                >
                  <div className="w-4 h-full flex items-center justify-center">
                    <i className={showPass ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                  </div>
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-100">
                <div className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-error-warning-line text-red-500 text-sm" />
                </div>
                <p className="text-red-600 text-xs">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-2.5 rounded-lg bg-[#18181B] text-white text-sm font-medium hover:bg-[#27272A] disabled:opacity-60 transition-all cursor-pointer whitespace-nowrap mt-2"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  登录中...
                </span>
              ) : '登录'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-[#9CA3AF] mt-6">
          &larr; <a href="/" className="hover:text-[#6B7280] transition-colors">返回官网</a>
        </p>
      </div>
    </div>
  );
}
